import React, { useCallback, useEffect, useMemo, useState } from "react";

import WarningIcon from "@mui/icons-material/Warning";
import { InputAdornment } from "@mui/material";
import { Stack } from "@mui/system";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { addMinutes, format, milliseconds, parseISO } from "date-fns";
import { truncate } from "lodash";
import { useHistory, useLocation } from "react-router-dom";

import useClientProfile from "@fartherfinance/frontend/api/Entity/hooks/useClientProfile";
import useGetProposals, {
  QueryString,
} from "@fartherfinance/frontend/api/Investment/hooks/useGetProposals";
import {
  ClientId,
  Custodian,
  CustodianAccountId,
  custodianOptions,
} from "@fartherfinance/frontend/api/Types";

import {
  Params,
  ProposalFilter,
  ProposalFilterOptions,
  ProposalLocationState,
} from "../types";
import searchIcon from "@src/assets/svg/search_icon.svg";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import CustomDropdownBase from "@src/sharedComponents/CustomDropdownBase/CustomDropdownBase";
import DatePicker from "@src/sharedComponents/DatePicker/DatePicker";
import Select from "@src/sharedComponents/Select/Select";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import PaginatedTable from "@src/sharedComponents/Table/PaginatedTable";
import { Cell, Column, Row } from "@src/sharedComponents/Table/Types";
import TextInput from "@src/sharedComponents/TextInput/TextInput";
import Tooltip from "@src/sharedComponents/Tooltip/Tooltip";

import styles from "./ProposalsTable.module.css";

const UserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const EasternTimeZone = "America/New_York";

const UseEastern = true;

const timeSuffix = UseEastern ? " (East)" : " (Local)";

type CustodianOptionsWithReset = Custodian | "All Custodians";

type Keys =
  | "proposalId"
  | "createdAt"
  | "expiresAt"
  | "clientIds"
  | "custodianAccountIds"
  | "portfolioModelName"
  | "status"
  | "bundleStatus";

const columns: Column<Keys>[] = [
  { accessor: "proposalId", label: "Proposal Id", sortable: false },
  { accessor: "createdAt", label: "Created", sortable: false },
  { accessor: "expiresAt", label: "Expires", sortable: false },
  { accessor: "clientIds", label: "User (Client)", sortable: false },
  { accessor: "custodianAccountIds", label: "Account ID", sortable: false },
  {
    accessor: "portfolioModelName",
    label: "Target Portfolio",
    sortable: false,
  },
  { accessor: "status", label: "Status", sortable: false },
  { accessor: "bundleStatus", label: "Bundle Status", sortable: false },
];

type ProposalSelectors = Pick<
  QueryString,
  | "expiresAtGt"
  | "expiresAtLt"
  | "validFromLt"
  | "bundleStatus"
  | "proposalStatus"
  | "bundleStatusNot"
>;

const ProposalsTable: React.FC = () => {
  const history = useHistory<ProposalLocationState>();

  const {
    setParams,
    params: { custodianAccountId, createdAtGt, createdAtLt },
    state,
  } = useQueryParams();

  const [page, setPage] = useState(state?.page ?? 1);
  const [pageSize, _setPageSize] = useState(20);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [selectedCustodian, setSelectedCustodian] = useState<Custodian | null>(
    state?.custodian ?? null
  );
  const [newCustodianAccountId, setNewCustodianAccountId] =
    useState<CustodianAccountId | null>(custodianAccountId ?? null);
  const [newCreatedAtGt, setNewCreatedAtGt] = useState<Date | null>(
    createdAtGt ?? null
  );
  const [newCreatedAtLt, setNewCreatedAtLt] = useState<Date | null>(
    createdAtLt ?? null
  );
  const [isCreatedAtLtInvalid, setIsCreatedAtLtInvalid] = useState(false);
  const [isCreatedAtGtInvalid, setIsCreatedAtGtInvalid] = useState(false);

  const handleCustodianAccountIdEntry = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newId = CustodianAccountId.parse(
        e.target.value.trim() satisfies string
      );

      if (newId === "") {
        setNewCustodianAccountId(null);
      } else {
        setNewCustodianAccountId(newId);
      }
      setPage(1);
    },
    []
  );

  const handleCustodianChange = useCallback(
    (custodian: CustodianOptionsWithReset | null) => {
      if (custodian === "All Custodians") {
        setSelectedCustodian(null);
      } else {
        setSelectedCustodian(custodian);
      }

      // Always reset account ID when custodian changes
      setNewCustodianAccountId(null);
      setPage(1);
    },
    []
  );

  const handleCreatedAtGtChange = useCallback((date: Date | null) => {
    setNewCreatedAtGt(date);
    if (date && date > new Date()) {
      setIsCreatedAtGtInvalid(true);
    } else {
      setIsCreatedAtGtInvalid(false);
    }
  }, []);

  const handleCreatedAtLtChange = useCallback((date: Date | null) => {
    setNewCreatedAtLt(date);
    if (date && date > new Date()) {
      setIsCreatedAtLtInvalid(true);
    } else {
      setIsCreatedAtLtInvalid(false);
    }
  }, []);

  const updateParams = useCallback(() => {
    setParams({
      custodianAccountId: newCustodianAccountId,
      createdAtGt: newCreatedAtGt,
      createdAtLt: newCreatedAtLt,
    });
  }, [newCustodianAccountId, newCreatedAtGt, newCreatedAtLt, setParams]);

  const handleFilterClick = useCallback(() => {
    updateParams();
    setShowFilter(false);
    setPage(1);
  }, [updateParams]);

  const activeProposals = useMemo<ProposalSelectors>(
    () => ({
      expiresAtGt: "now",
      validFromLt: "now",
      proposalStatus: ["Proposed"],
    }),
    []
  );

  const immaterialProposals = useMemo<ProposalSelectors>(
    () => ({
      expiresAtGt: "now",
      validFromLt: "now",
      proposalStatus: ["Immaterial"],
    }),
    []
  );

  const expiredProposedProposals = useMemo<ProposalSelectors>(
    () => ({
      expiresAtLt: "now",
      validFromLt: "now",
      proposalStatus: ["Proposed"],
    }),
    []
  );

  const expiredProposals = useMemo<ProposalSelectors>(
    () => ({
      expiresAtLt: "now",
      validFromLt: "now",
      proposalStatus: ["Proposed", "Immaterial"],
    }),
    []
  );

  const submittedProposals = useMemo<ProposalSelectors>(
    () => ({
      bundleStatus: ["Submitted"],
    }),
    []
  );

  const skippedProposals = useMemo<ProposalSelectors>(
    () => ({
      proposalStatus: ["Skipped"],
    }),
    []
  );

  const [proposalFilter, setProposalFilter] = useState<ProposalFilter>(
    state?.proposalFilter ?? "Active Proposals"
  );

  const queryString = useMemo((): QueryString => {
    const base: QueryString = {
      page,
      pageSize,
      orderBy: "createdAt asc",
      custodianAccountId: custodianAccountId ?? undefined,
      createdAtGt: createdAtGt ?? undefined,
      createdAtLt: createdAtLt ?? undefined,
      custodian: selectedCustodian ?? undefined,
    };

    switch (proposalFilter) {
      case "Active Proposals":
        return { ...base, ...activeProposals };

      case "Active Immaterial Proposals":
        return { ...base, ...immaterialProposals };

      case "All Expired Proposals":
        // switch order to descending otherwise we will see super old ones first
        return { ...base, ...expiredProposals, orderBy: "createdAt desc" };

      case "All Expired Proposed Proposals":
        // switch order to descending otherwise we will see super old ones first
        return {
          ...base,
          ...expiredProposedProposals,
          orderBy: "createdAt desc",
        };

      case "All Submitted Proposals":
        return { ...base, ...submittedProposals };

      case "All Proposals":
        // switch order to descending otherwise we will see super old ones first
        return { ...base, orderBy: "createdAt desc" };

      case "All Skipped Proposals":
        // switch order to descending otherwise we will see super old ones first
        return { ...base, ...skippedProposals, orderBy: "createdAt desc" };
    }
  }, [
    activeProposals,
    expiredProposals,
    expiredProposedProposals,
    immaterialProposals,
    page,
    pageSize,
    proposalFilter,
    submittedProposals,
    custodianAccountId,
    createdAtGt,
    createdAtLt,
    selectedCustodian,
    skippedProposals,
  ]);

  const auth = useAdvisorRequestAuth();
  const proposals = useGetProposals(queryString, auth, false, {
    refetchInterval: milliseconds({ seconds: 10 }),
  });

  useEffect(() => {
    setUpdatedAt(new Date());
  }, [proposals]);

  const data = useMemo((): Row<Keys, Cell>[] => {
    if (proposals.data === undefined) {
      return [];
    }

    const proposalIdList = proposals.data.data.map(
      (proposal) => proposal.proposalId
    );

    return proposals.data.data.map((proposal) => ({
      onClick: () => {
        const newUrl = `/Advisor/FinancialOperations/TradeProposals/${proposal.proposalId}`;
        history.push(newUrl, {
          proposalIds: proposalIdList,
          proposalFilter,
          page,
          createdAtGt,
          createdAtLt,
          custodianAccountId,
          custodian: selectedCustodian,
        });
      },
      key: proposal.proposalId,
      proposalId: proposal.proposalId,
      createdAt: `${proposal.createdAt.toLocaleString("en-US", {
        timeZone: UseEastern ? EasternTimeZone : UserTimeZone,
        dateStyle: "short",
        timeStyle: "short",
      })}${timeSuffix}`,
      expiresAt:
        proposal.expiresAt === null
          ? "Never"
          : `${proposal.expiresAt.toLocaleString("en-US", {
              timeZone: UseEastern ? EasternTimeZone : UserTimeZone,
              dateStyle: "short",
              timeStyle: "short",
            })}${timeSuffix}`,
      clientIds: {
        value: (
          <>
            {proposal.clientIds.map((clientId) => (
              <ClientDetails key={clientId} clientId={clientId} />
            ))}
          </>
        ),
      },
      custodianAccountIds:
        proposal.custodianAccountIds.length === 1
          ? proposal.custodianAccountIds[0]
          : {
              value: (
                <>
                  {proposal.custodianAccountIds.map((accountId) => (
                    <div key={accountId}>{accountId}</div>
                  ))}
                </>
              ),
            },
      portfolioModelName: truncate(proposal.portfolioModelName, { length: 30 }),
      status: {
        value: (
          <>
            {proposal.portfolioPlanAssignmentIntent === "TradingOff" ? (
              <span>
                <Tooltip
                  placement="top"
                  tooltipText="Trading is turned off in the Farther portal."
                >
                  <WarningIcon />
                </Tooltip>
              </span>
            ) : null}

            <span>{proposal.status}</span>
          </>
        ),
      },
      bundleStatus: proposal.tradeProposalBundle?.bundleStatus ?? "",
    }));
  }, [
    proposals.data,
    history,
    proposalFilter,
    page,
    createdAtGt,
    createdAtLt,
    custodianAccountId,
    selectedCustodian,
  ]);

  const custodianOptionsWithReset = useMemo(
    (): CustodianOptionsWithReset[] => ["All Custodians", ...custodianOptions],
    []
  );

  if (proposals.isLoading || proposals.hasError) {
    return (
      <Stack direction={"column"} gap={2}>
        <Stack
          direction={"row"}
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction={"row"} alignItems="center" spacing={2}>
            <Select
              className={styles.selectDropdownItem}
              menuClassName={styles.selectProposalMenu}
              value={proposalFilter}
              onChange={setProposalFilter}
              options={ProposalFilterOptions}
              renderValue={(value) => value}
            />
            <CustomDropdownBase
              className={styles.dropdownBase}
              curItem={{ value: "Filters" }}
              showDropdown={showFilter}
              setShowDropdown={setShowFilter}
              isDisabled={false}
              hideIcon={false}
              menuWidth={348}
            >
              <div className={styles.dropdownBaseChildren}>
                <Select
                  value={selectedCustodian ?? "All Custodians"}
                  onChange={handleCustodianChange}
                  options={custodianOptionsWithReset}
                  renderValue={(value) => value ?? "Select Custodian"}
                  placeholder="Select Custodian"
                />
                <TextInput
                  className={styles.accountIdInput}
                  value={newCustodianAccountId ?? ""}
                  placeholder={"Search by Custodian Account ID"}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position={"start"}>
                        <img src={searchIcon} alt="Search Icon" />
                      </InputAdornment>
                    ),
                  }}
                  onChange={handleCustodianAccountIdEntry}
                />
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    className={`${styles.datePicker} ${
                      isCreatedAtLtInvalid
                        ? styles.invalidCreatedBeforeDate
                        : ""
                    }`}
                    label="Created Before"
                    value={newCreatedAtLt}
                    onChange={handleCreatedAtLtChange}
                    onError={(error) => {
                      if (error) {
                        setIsCreatedAtLtInvalid(true);
                      } else {
                        setIsCreatedAtLtInvalid(false);
                      }
                    }}
                  />
                  <DatePicker
                    className={`${styles.datePicker} ${
                      isCreatedAtGtInvalid ? styles.invalidCreatedAfterDate : ""
                    }`}
                    label="Created After"
                    value={newCreatedAtGt}
                    onChange={handleCreatedAtGtChange}
                    onError={(error) => {
                      if (error) {
                        setIsCreatedAtGtInvalid(true);
                      } else {
                        setIsCreatedAtGtInvalid(false);
                      }
                    }}
                  />
                </LocalizationProvider>
                <Stack
                  direction="row"
                  spacing={2}
                  justifyContent="center"
                  className={styles.buttonContainer}
                >
                  <Button
                    variant="outlined"
                    buttonType="secondary"
                    text="Cancel"
                    onClick={() => {
                      setShowFilter(false);
                    }}
                  />
                  <Button
                    variant="contained"
                    buttonType="primary"
                    text="Apply"
                    onClick={handleFilterClick}
                    disabled={isCreatedAtGtInvalid || isCreatedAtLtInvalid}
                  />
                </Stack>
              </div>
            </CustomDropdownBase>
          </Stack>
          <div>
            Updated at{" "}
            {updatedAt === null ? "Not yet" : format(updatedAt, "PPpp")} (local)
          </div>
        </Stack>

        <PaginatedTable
          isLoading={true}
          columns={columns}
          rows={[]}
          pagination={{
            currentPage: page,
            totalPages: 1,
            pageSize: pageSize,
            totalCount: 0,
          }}
          onPageChange={() => {}}
        />
      </Stack>
    );
  }

  const totalPages = Math.ceil(
    proposals.data.pagination.totalCount / proposals.data.pagination.pageSize
  );

  const effectivePageSize = Math.min(
    proposals.data.pagination.pageSize,
    proposals.data.pagination.totalCount
  );

  return (
    <Stack direction={"column"} gap={2}>
      <Stack
        direction={"row"}
        justifyContent="space-between"
        alignItems="center"
      >
        <Stack direction={"row"} alignItems="center" spacing={2}>
          <Select
            className={styles.selectDropdownItem}
            menuClassName={styles.selectProposalMenu}
            value={proposalFilter}
            onChange={setProposalFilter}
            options={ProposalFilterOptions}
            renderValue={(value) => value}
          />
          <CustomDropdownBase
            className={styles.dropdownBase}
            curItem={{ value: "Filters" }}
            showDropdown={showFilter}
            setShowDropdown={setShowFilter}
            isDisabled={false}
            hideIcon={false}
            menuWidth={348}
          >
            <div className={styles.dropdownBaseChildren}>
              <Select
                menuClassName={styles.custodianSelectMenu}
                value={selectedCustodian ?? "All Custodians"}
                onChange={handleCustodianChange}
                options={custodianOptionsWithReset}
                renderValue={(value) => value ?? "Select Custodian"}
                placeholder="Select Custodian"
              />
              <TextInput
                className={styles.accountIdInput}
                value={newCustodianAccountId ?? ""}
                placeholder={"Search by Custodian Account ID"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position={"start"}>
                      <img src={searchIcon} alt="Search Icon" />
                    </InputAdornment>
                  ),
                }}
                onChange={handleCustodianAccountIdEntry}
              />
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  className={`${styles.datePicker} ${
                    isCreatedAtLtInvalid ? styles.invalidCreatedBeforeDate : ""
                  }`}
                  label="Created Before"
                  value={newCreatedAtLt}
                  onChange={handleCreatedAtLtChange}
                  onError={(error) => {
                    if (error) {
                      setIsCreatedAtLtInvalid(true);
                    } else {
                      setIsCreatedAtLtInvalid(false);
                    }
                  }}
                />
                <DatePicker
                  className={`${styles.datePicker} ${
                    isCreatedAtGtInvalid ? styles.invalidCreatedAfterDate : ""
                  }`}
                  label="Created After"
                  value={newCreatedAtGt}
                  onChange={handleCreatedAtGtChange}
                  onError={(error) => {
                    if (error) {
                      setIsCreatedAtGtInvalid(true);
                    } else {
                      setIsCreatedAtGtInvalid(false);
                    }
                  }}
                />
              </LocalizationProvider>
              <Stack
                direction="row"
                spacing={2}
                justifyContent="center"
                className={styles.buttonContainer}
              >
                <Button
                  variant="outlined"
                  buttonType="secondary"
                  text="Cancel"
                  onClick={() => {
                    setShowFilter(false);
                  }}
                />
                <Button
                  variant="contained"
                  buttonType="primary"
                  text="Apply"
                  onClick={handleFilterClick}
                  disabled={isCreatedAtGtInvalid || isCreatedAtLtInvalid}
                />
              </Stack>
            </div>
          </CustomDropdownBase>
        </Stack>
        <div>
          Updated at{" "}
          {updatedAt === null ? "Not yet" : format(updatedAt, "PPpp")} (local)
        </div>
      </Stack>

      <PaginatedTable
        isLoading={false}
        columns={columns}
        rows={data}
        pagination={{
          currentPage: page,
          totalPages: totalPages,
          pageSize: effectivePageSize,
          totalCount: proposals.data.pagination.totalCount,
          prevPage: page === 1 ? undefined : page - 1,
          nextPage: page === totalPages ? undefined : page + 1,
        }}
        onPageChange={(newPage) => {
          setPage(newPage);
        }}
      />
    </Stack>
  );
};

export default ProposalsTable;

interface ClientDetailsProps {
  clientId: ClientId;
}

const ClientDetails: React.FC<ClientDetailsProps> = (props) => {
  const auth = useAdvisorRequestAuth();
  const profile = useClientProfile(
    props.clientId,
    auth === null ? null : { ...auth, clientId: props.clientId }
  );

  if (profile.isLoading || profile.hasError) {
    return (
      <div>
        <Skeleton />
      </div>
    );
  }

  const fullName = `${profile.data.investorProfile.personalDetails.name.first} ${profile.data.investorProfile.personalDetails.name.last}`;

  return (
    <Tooltip placement="top" tooltipText={props.clientId}>
      <div>{truncate(fullName, { length: 20 })}</div>
    </Tooltip>
  );
};

type QueryParams = {
  params: Params;
  setParams: (params: Params) => void;
  state: ProposalLocationState;
};

function useQueryParams(): QueryParams {
  const {
    search: stringSearch,
    pathname,
    state,
  } = useLocation<ProposalLocationState>();

  const urlSearch = useMemo(
    () => new URLSearchParams(stringSearch),
    [stringSearch]
  );

  // no need to memo as strings are always deeply equal
  const custodianAccountId =
    urlSearch.get("custodianAccountId") ?? state?.custodianAccountId;
  const createdAtGt =
    urlSearch.get("createdAtGt") ?? state?.createdAtGt?.toISOString();
  const createdAtLt =
    urlSearch.get("createdAtLt") ?? state?.createdAtLt?.toISOString();

  const history = useHistory<ProposalLocationState>();

  const setParams = useCallback(
    ({ custodianAccountId, createdAtGt, createdAtLt }: Params) => {
      const clone = new URLSearchParams(urlSearch);

      if (custodianAccountId) {
        clone.set("custodianAccountId", custodianAccountId);
      } else {
        clone.delete("custodianAccountId");
      }

      if (createdAtGt) {
        const yearFormated = format(createdAtGt, "yyyy-MM-dd"); // iso8601 compatible date (minus time)
        clone.set("createdAtGt", yearFormated);
      } else {
        clone.delete("createdAtGt");
      }

      if (createdAtLt) {
        const yearFormated = format(createdAtLt, "yyyy-MM-dd"); // iso8601 compatible date (minus time)
        clone.set("createdAtLt", yearFormated);
      } else {
        clone.delete("createdAtLt");
      }

      history.push({
        pathname,
        search: `?${clone.toString()}`,
      });
    },
    [history, pathname, urlSearch]
  );

  const params: Params = useMemo(() => {
    const createdAtGtLocal = createdAtGt ? parseISO(createdAtGt) : null;
    const createdAtLtLocal = createdAtLt ? parseISO(createdAtLt) : null;

    // add offset to the dates to reset to GMT
    const createdAtGtOffset = createdAtGtLocal
      ? addMinutes(createdAtGtLocal, createdAtGtLocal.getTimezoneOffset())
      : createdAtGtLocal;
    const createdAtLtOffset = createdAtLtLocal
      ? addMinutes(createdAtLtLocal, createdAtLtLocal.getTimezoneOffset())
      : createdAtLtLocal;

    return {
      custodianAccountId:
        custodianAccountId === null || custodianAccountId === undefined
          ? null
          : CustodianAccountId.parse(custodianAccountId satisfies string),
      createdAtGt: createdAtGtOffset,
      createdAtLt: createdAtLtOffset,
    };
  }, [custodianAccountId, createdAtGt, createdAtLt]);

  return useMemo(
    () => ({ params, setParams, state }),
    [params, setParams, state]
  );
}
